import React from 'react';
import { Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Comparison, Pick, PublishedModelRow, RunSummary, fmtCredits, fmtPctValue, fmtSeconds } from '../../services/testsApi';
import { PICK_LABELS, Score, modelLabel } from './common';

/** One model's results, from a run summary or a published route. Optional fields hide their column when no row has them. */
export interface ComparisonRow {
  model: string;
  accuracy: number | null;
  pass_rate: number | null;
  success_rate: number | null;
  p50_s: number | null;
  p95_s: number | null;
  credits_per_run: number | null;
  agreement?: number | null;
  within_deadline?: number | null;
  vs_default?: Comparison | null;
  skipped?: number;
  skip_reason?: string;
  problems?: string[];
}

type PickSet = { recommended: Pick | null; quality: Pick | null; budget: Pick | null } | null | undefined;

export function rowsFromSummary(summary: RunSummary, order?: string[]): ComparisonRow[] {
  const problems = Object.fromEntries((summary.rankings?.balanced || []).filter(r => !r.eligible).map(r => [r.model, r.problems]));
  const models = order?.length ? order.filter(m => summary.models[m]) : Object.keys(summary.models);
  return models.map(m => {
    const s = summary.models[m];
    return {
      model: m,
      accuracy: s.accuracy.score,
      pass_rate: s.accuracy.pass_rate,
      success_rate: s.reliability.success_rate,
      p50_s: s.speed.p50_s,
      p95_s: s.speed.p95_s,
      credits_per_run: s.cost.credits_per_run,
      agreement: s.consistency.cases_repeated ? s.consistency.agreement : null,
      within_deadline: summary.deadline_s !== null ? s.speed.within_deadline : undefined,
      vs_default: summary.comparisons?.[m] ?? null,
      skipped: s.skipped,
      skip_reason: s.skip_reasons[0],
      problems: problems[m],
    };
  });
}

export const rowsFromPublished = (models: PublishedModelRow[]): ComparisonRow[] =>
  [...models]
    .sort((a, b) => (b.accuracy ?? -1) - (a.accuracy ?? -1))
    .map(m => ({
      model: m.model,
      accuracy: m.accuracy,
      pass_rate: m.pass_rate,
      success_rate: m.success_rate,
      p50_s: m.p50_s,
      p95_s: m.p95_s,
      credits_per_run: m.credits_per_run,
    }));

const best = (rows: ComparisonRow[], key: 'accuracy' | 'p95_s' | 'credits_per_run', lowest: boolean) => {
  const values = rows.map(r => r[key]).filter((v): v is number => v !== null && v !== undefined);
  if (values.length < 2) return null;
  return lowest ? Math.min(...values) : Math.max(...values);
};

const Best: React.FC<{ on: boolean; children: React.ReactNode }> = ({ on, children }) =>
  on ? (
    <Tooltip title="Best of the models tested">
      <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
        {children}
      </Typography>
    </Tooltip>
  ) : (
    <>{children}</>
  );

/** Every tested model side by side: accuracy, reliability, speed and cost, with the picks marked. */
export const ModelComparisonTable: React.FC<{
  rows: ComparisonRow[];
  defaultModel?: string | null;
  picks?: PickSet;
  bar?: number;
  deadline?: number | null;
  onRowClick?: (model: string) => void;
}> = ({ rows, defaultModel, picks, bar = 85, deadline, onRowClick }) => {
  const showConsistency = rows.some(r => r.agreement !== undefined);
  const showOnTime = rows.some(r => r.within_deadline !== undefined);
  const showVsDefault = rows.some(r => r.vs_default !== undefined);
  const bestAccuracy = best(rows, 'accuracy', false);
  const fastest = best(rows, 'p95_s', true);
  const cheapest = best(rows, 'credits_per_run', true);
  const pickRoles = (['recommended', 'quality', 'budget'] as const).filter(k => picks?.[k]);

  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Model</TableCell>
            <TableCell align="right">
              <Tooltip title="Average judge score, 0–100">
                <span>Accuracy</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              <Tooltip title="Share of cases at or above their pass score">
                <span>Passing</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              <Tooltip title="Calls that returned without an error">
                <span>Success</span>
              </Tooltip>
            </TableCell>
            {showConsistency && (
              <TableCell align="right">
                <Tooltip title="Share of repeated cases where every run agreed on pass or fail">
                  <span>Consistent</span>
                </Tooltip>
              </TableCell>
            )}
            <TableCell align="right">
              <Tooltip title="Typical time per call (median)">
                <span>Typical time</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              <Tooltip title="95% of calls finish within this time">
                <span>Slow calls (p95)</span>
              </Tooltip>
            </TableCell>
            {showOnTime && (
              <TableCell align="right">
                <Tooltip title={deadline ? `Calls within the ${deadline}s platform deadline` : 'Calls within the deadline'}>
                  <span>On time</span>
                </Tooltip>
              </TableCell>
            )}
            <TableCell align="right">Credits per call</TableCell>
            {showVsDefault && (
              <TableCell align="right">
                <Tooltip title="Average score difference per case against the current default, with a 95% range. Bold when the range doesn't cross zero.">
                  <span>vs default</span>
                </Tooltip>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => {
            const c = r.vs_default;
            return (
              <TableRow key={r.model} hover sx={onRowClick ? { cursor: 'pointer' } : undefined} onClick={onRowClick ? () => onRowClick(r.model) : undefined}>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {modelLabel(r.model)}
                    </Typography>
                    {r.model === defaultModel && <Chip size="small" label="current default" sx={{ height: 18 }} />}
                    {pickRoles
                      .filter(k => picks?.[k]?.model === r.model)
                      .map(k => (
                        <Tooltip key={k} title={picks?.[k]?.why || PICK_LABELS[k].help}>
                          <Chip size="small" color={PICK_LABELS[k].color} label={PICK_LABELS[k].label} sx={{ height: 18, cursor: 'help' }} />
                        </Tooltip>
                      ))}
                    {!!r.problems?.length && (
                      <Tooltip title={r.problems.join('; ')}>
                        <Chip size="small" variant="outlined" color="warning" label="not eligible" sx={{ height: 18, cursor: 'help' }} />
                      </Tooltip>
                    )}
                  </Stack>
                  {!!r.skipped && (
                    <Typography variant="caption" color="text.secondary">
                      {r.skipped} skipped{r.skip_reason ? `: ${r.skip_reason}` : ''}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Best on={r.accuracy !== null && r.accuracy === bestAccuracy}>
                    <Score value={r.accuracy} bar={bar} />
                  </Best>
                </TableCell>
                <TableCell align="right">{fmtPctValue(r.pass_rate)}</TableCell>
                <TableCell align="right">{fmtPctValue(r.success_rate)}</TableCell>
                {showConsistency && <TableCell align="right">{r.agreement !== null && r.agreement !== undefined ? fmtPctValue(r.agreement) : '–'}</TableCell>}
                <TableCell align="right">{fmtSeconds(r.p50_s)}</TableCell>
                <TableCell align="right">
                  <Best on={r.p95_s !== null && r.p95_s === fastest}>{fmtSeconds(r.p95_s)}</Best>
                </TableCell>
                {showOnTime && <TableCell align="right">{r.within_deadline !== undefined ? fmtPctValue(r.within_deadline) : '–'}</TableCell>}
                <TableCell align="right">
                  <Best on={r.credits_per_run !== null && r.credits_per_run === cheapest}>{fmtCredits(r.credits_per_run)}</Best>
                </TableCell>
                {showVsDefault && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {r.model === defaultModel || !c ? (
                      '–'
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: c.significant ? 700 : 400, color: c.significant ? (c.diff > 0 ? 'success.main' : 'error.main') : undefined }}
                      >
                        {c.diff > 0 ? '+' : ''}
                        {c.diff.toFixed(1)}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({c.low.toFixed(1)} to {c.high.toFixed(1)})
                        </Typography>
                      </Typography>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};
